import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import AgentDryRunPanel from "@/components/salgsmuligheter/agents/AgentDryRunPanel";
import AgentSegmentSection from "@/components/salgsmuligheter/agents/AgentSegmentSection";
import CustomerMultiSelect from "@/components/salgsmuligheter/agents/CustomerMultiSelect";
import { useCustomers } from "@/hooks/use-customers";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useMonitoredCustomers } from "@/hooks/use-monitoring-sources";
import { useDryRunSignalAgent } from "@/hooks/use-run-signal-agents";
import { useCreateSignalAgent, useUpdateSignalAgent } from "@/hooks/use-signal-agents";
import { useSystemConfigNumber } from "@/hooks/use-system-config";
import { FREQUENCY_LABELS } from "@/lib/monitoring-sources";
import { pickDryRunCustomers, SEGMENT_SOURCES, validateSignalAgentInput, type SignalAgentInput } from "@/lib/signal-agent-calc";
import {
  isSignalTypeKey,
  SIGNAL_SOURCE_LABELS,
  SIGNAL_STRENGTH_LABELS,
  SIGNAL_TYPE_KEYS,
  SIGNAL_TYPES,
} from "@/lib/signal-types";
import type { DBSignalAgent, SignalAgentSource, SignalFrequency, SignalStrength } from "@/lib/db-types";

// Kildene en egen agent kan lese. «news» er ikke i bruk i fase 3.
const SOURCE_OPTIONS: SignalAgentSource[] = ["website", "instagram", "facebook", "linkedin", "brreg"];
// Typer som lages av kode/SQL kan ikke velges for egne agenter.
const TYPE_OPTIONS = SIGNAL_TYPE_KEYS.filter((k) => !SIGNAL_TYPES[k].deterministic);

function emptyForm(): SignalAgentInput {
  return {
    name: "",
    description: null,
    scope_type: "monitored",
    customer_ids: null,
    segment: null,
    sources: ["website"],
    instruction: "",
    signal_type: "custom",
    contact_reason: "",
    default_strength: "medium",
    frequency: "weekly",
    is_active: true,
    cost_cap_nok_month: null,
  };
}

function fromAgent(a: DBSignalAgent): SignalAgentInput {
  return {
    name: a.name, description: a.description, scope_type: a.scope_type, customer_ids: a.customer_ids, segment: a.segment,
    sources: a.sources, instruction: a.instruction, signal_type: a.signal_type, contact_reason: a.contact_reason,
    default_strength: a.default_strength, frequency: a.frequency, is_active: a.is_active, cost_cap_nok_month: a.cost_cap_nok_month,
  };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Agenten som redigeres, eller undefined for ny. */
  agent?: DBSignalAgent;
}

/** Ny eller redigér agent. Systemagenter kan bare få justert styrke (av admin); pause og aktivering skjer i tabellen. */
export default function SignalAgentFormDialog({ open, onOpenChange, agent }: Props) {
  const [form, setForm] = useState<SignalAgentInput>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const create = useCreateSignalAgent();
  const update = useUpdateSignalAgent();
  const dryRun = useDryRunSignalAgent();
  const { data: customers = [] } = useCustomers();
  const { data: monitored = [] } = useMonitoredCustomers({ enabled: open });
  const { data: defaultCap } = useSystemConfigNumber("crm_agent_cost_cap_default_nok");
  const isSystem = !!agent?.is_system;
  const leadsEnabled = useFeatureFlag("feature_crm_leads_moter");
  const isSegment = form.scope_type === "segment";
  const sourceOptions = isSegment ? SOURCE_OPTIONS.filter((s) => SEGMENT_SOURCES.includes(s)) : SOURCE_OPTIONS;

  useEffect(() => {
    if (open) {
      setForm(agent ? fromAgent(agent) : emptyForm());
      setError(null);
      dryRun.reset();
    }
    // dryRun er stabil nok; effekten skal bare kjøre når dialogen åpnes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agent]);

  const patch = (p: Partial<SignalAgentInput>) => setForm((f) => ({ ...f, ...p }));
  const toggleSource = (s: SignalAgentSource, on: boolean) =>
    patch({ sources: on ? [...form.sources, s] : form.sources.filter((x) => x !== s) });

  const dryRunCustomerIds = useMemo(() => pickDryRunCustomers(form, monitored), [form, monitored]);
  const dryRunNames = dryRunCustomerIds.map((id) => customers.find((c) => c.id === id)?.name ?? "Kunde");

  const runDry = () => {
    const invalid = validateSignalAgentInput(form);
    if (invalid) return setError(invalid);
    setError(null);
    dryRun.mutate({ agent: form, customerIds: isSegment ? [] : dryRunCustomerIds });
  };

  const save = () => {
    // Systemagenter har fast definisjon: bare styrke sendes. Pause og aktivering skjer i tabellen,
    // så en pause gjort mens dialogen var åpen ikke blir overskrevet.
    const payload = isSystem ? { default_strength: form.default_strength } : form;
    const invalid = isSystem ? null : validateSignalAgentInput(form);
    if (invalid) return setError(invalid);
    setError(null);
    const done = { onSuccess: () => onOpenChange(false) };
    if (agent) update.mutate({ id: agent.id, ...payload }, done);
    else create.mutate(form, done);
  };

  const onType = (type: string) => {
    const meta = isSignalTypeKey(type) ? SIGNAL_TYPES[type] : null;
    patch({
      signal_type: type,
      default_strength: meta?.strength ?? form.default_strength,
      contact_reason: type === "custom" ? form.contact_reason : meta?.contactReason ?? form.contact_reason,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{agent ? (isSystem ? `Systemagent: ${agent.name}` : "Rediger agent") : "Ny agent"}</DialogTitle>
          <DialogDescription>
            {isSystem
              ? "Systemagenter har fast instruks. Du kan justere styrke her, og pause eller aktivere dem i tabellen."
              : "Agenten leser kildene for kundene i utvalget og lager et ferdig kort når den finner noe."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!isSystem && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="agent-name">Navn</Label>
                <Input id="agent-name" value={form.name} onChange={(e) => patch({ name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="agent-description">Beskrivelse</Label>
                <Input id="agent-description" value={form.description ?? ""} onChange={(e) => patch({ description: e.target.value || null })} />
              </div>
              <div className="space-y-1.5">
                <Label>Hvilke kunder</Label>
                <Select
                  value={form.scope_type}
                  onValueChange={(v) =>
                    patch({
                      scope_type: v as SignalAgentInput["scope_type"],
                      customer_ids: v === "customers" ? form.customer_ids : null,
                      segment: v === "segment" ? form.segment : null,
                      // Segmentagenter leser bare nettsiden og Brønnøysund.
                      sources: v === "segment" ? form.sources.filter((s) => SEGMENT_SOURCES.includes(s)) : form.sources,
                    })}
                >
                  <SelectTrigger aria-label="Hvilke kunder"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monitored">Alle overvåkede</SelectItem>
                    <SelectItem value="customers">Utvalgte kunder</SelectItem>
                    <SelectItem value="segment" disabled={!leadsEnabled && form.scope_type !== "segment"}>Eksternt segment{leadsEnabled ? "" : " (Kommer med Leads)"}</SelectItem>
                  </SelectContent>
                </Select>
                {form.scope_type === "customers" && (
                  <CustomerMultiSelect value={form.customer_ids ?? []} onChange={(ids) => patch({ customer_ids: ids })} />
                )}
                {isSegment && (
                  <AgentSegmentSection key={`${agent?.id ?? "ny"}:${open}`} initial={form.segment} onChange={(segment) => { patch({ segment }); setError(null); }} onInvalid={(message) => { patch({ segment: null }); setError(message); }} />
                )}
              </div>
              <fieldset className="space-y-1.5">
                <legend className="text-sm font-medium">Kilder</legend>
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  {sourceOptions.map((s) => (
                    <label key={s} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={form.sources.includes(s)} onCheckedChange={(c) => toggleSource(s, c === true)} aria-label={SIGNAL_SOURCE_LABELS[s]} />
                      {SIGNAL_SOURCE_LABELS[s]}
                    </label>
                  ))}
                </div>
              </fieldset>
              <div className="space-y-1.5">
                <Label htmlFor="agent-instruction">Instruks (minst 20 tegn)</Label>
                <Textarea id="agent-instruction" rows={4} value={form.instruction} onChange={(e) => patch({ instruction: e.target.value })} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Type signal</Label>
                  <Select value={form.signal_type} onValueChange={onType}>
                    <SelectTrigger aria-label="Type signal"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TYPE_OPTIONS.map((k) => <SelectItem key={k} value={k}>{SIGNAL_TYPES[k].label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="agent-reason">Kontaktårsak</Label>
                  <Input id="agent-reason" value={form.contact_reason} disabled={form.signal_type !== "custom"} onChange={(e) => patch({ contact_reason: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Frekvens</Label>
                  <Select value={form.frequency} onValueChange={(v) => patch({ frequency: v as SignalFrequency })}>
                    <SelectTrigger aria-label="Frekvens"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(FREQUENCY_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="agent-cap">Kostnadstak per måned (kr)</Label>
                  <Input
                    id="agent-cap"
                    inputMode="decimal"
                    value={form.cost_cap_nok_month ?? ""}
                    placeholder={defaultCap != null ? `Standard ${defaultCap}` : "Standard"}
                    onChange={(e) => {
                      const n = e.target.value.trim() === "" ? null : Number(e.target.value.replace(",", "."));
                      patch({ cost_cap_nok_month: n != null && Number.isFinite(n) ? n : null });
                    }}
                  />
                </div>
              </div>
            </>
          )}
          <div className="space-y-1.5">
            <Label>Styrke</Label>
            <Select value={form.default_strength} onValueChange={(v) => patch({ default_strength: v as SignalStrength })}>
              <SelectTrigger aria-label="Styrke" className="sm:w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(SIGNAL_STRENGTH_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          {!isSystem && (
            <div className="space-y-2">
              <Button type="button" variant="outline" size="sm" onClick={runDry} disabled={dryRun.isPending}>
                {isSegment ? "Prøvekjør på 3 kandidater" : "Prøvekjør på 3 kunder"}
              </Button>
              <AgentDryRunPanel isPending={dryRun.isPending} result={dryRun.data} customerNames={isSegment ? ["3 kandidater fra segmentet"] : dryRunNames} />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button onClick={save} disabled={create.isPending || update.isPending}>Lagre agent</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
